import { isPowerPointApiSupported } from '@/services/office/helpers'

// Finding the content add-in shapes on a slide. Extracted from services/office/resize so the
// auto-start ownership check (services/office/current-slide) can use it too: resize reaches
// @/vuex/store, and current-slide has to stay a leaf. Everything here depends on office/helpers
// alone.

// ShapeType values are PascalCase on the wire ("ContentApp"); compare case-insensitively so
// a host that reports a different casing — or a missing PowerPoint.ShapeType enum — still matches.
const isShapeType = (value, expected) => String(value).toLowerCase() === expected.toLowerCase()

const CONTENT_APP_TYPE = 'ContentApp'
const PLACEHOLDER_TYPE = 'Placeholder'

// Slide.shapes + Shape.type are 1.4; placeholderFormat is 1.8.
const PLACEHOLDER_FORMAT_API_VERSION = '1.8'

// There is no handle on the add-in's own shape — enumerate the slide's shapes and find it.
const isContentAppShape = (shape) => isShapeType(shape.type, CONTENT_APP_TYPE)
const isPlaceholderShape = (shape) => isShapeType(shape.type, PLACEHOLDER_TYPE)

// Inserting the add-in on a layout that has an empty content placeholder (Title and Content,
// Two Content, Comparison, ...) makes PowerPoint fill THAT placeholder rather than create a
// free-floating shape — and there is no way to opt out of it on insert. Such a shape reports
// type "Placeholder"; only placeholderFormat.containedType names it as the add-in. Without
// this branch every layout except Blank would look add-in-free.
//
// PowerPoint throws if placeholderFormat is loaded on a non-placeholder shape — hence the
// filter and the extra sync, which only the placeholder path pays for.
//
// Returns EVERY content add-in on the slide, not the first match: Office.js gives an add-in
// no handle on its own shape, so a slide carrying two of them is genuinely ambiguous and the
// caller must decide what that means (see resizeAddinShapeOnce).
//
// `shapes` must already have items/type loaded and synced.
export const findAddinShapes = async (context, shapes) => {
  const placeholders = shapes.items.filter(isPlaceholderShape)
  if (placeholders.length) {
    placeholders.forEach((shape) => shape.load('placeholderFormat/containedType'))
    await context.sync()
  }

  // containedType is null for an empty placeholder.
  return shapes.items.filter((shape) => isContentAppShape(shape)
    || (isPlaceholderShape(shape) && isShapeType(shape.placeholderFormat?.containedType, CONTENT_APP_TYPE)))
}

export const canReadSlideShapes = () => Boolean(window.PowerPoint && window.PowerPoint.run)
  && isPowerPointApiSupported(PLACEHOLDER_FORMAT_API_VERSION)

// AHA-48167: does the slide at `slideIndex` (1-based) host ANY content add-in? One batch.
// Resolves true/false; rejects if the batch errors — the caller bounds and handles it.
export const readSlideHostsContentAddinOnce = (slideIndex) => window.PowerPoint.run(async (context) => {
  const slide = context.presentation.slides.getItemAt(slideIndex - 1)
  slide.shapes.load('items/type')
  await context.sync()
  const addinShapes = await findAddinShapes(context, slide.shapes)
  return addinShapes.length > 0
})
